import { inject, isDevMode, provideAppInitializer, type Provider, type EnvironmentProviders } from '@angular/core';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-http-loader';
import { DEFAULT_LANG, LanguageService, SUPPORTED_LANGS } from './language.service';

/**
 * Call in each app's `app.config.ts` providers array to enable Vantage i18n.
 *
 * Registers the Transloco runtime (HTTP loader, `availableLangs` from the {@link SUPPORTED_LANGS}
 * enumerator, `defaultLang` {@link DEFAULT_LANG}, re-render on language change, prod mode off the
 * Angular dev-mode flag) plus an app initializer that restores the user's saved preference via
 * `LanguageService.init()`. Translation files live in each app's `public/i18n/<lang>.json`, one per
 * entry of `SUPPORTED_LANGS`.
 *
 * @returns the Transloco providers and the startup initializer, to spread into an app config.
 */
export function provideVantageI18n(): (Provider | EnvironmentProviders)[] {
  return [
    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_LANGS],
        defaultLang: DEFAULT_LANG,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    // Restore the user's saved language preference on startup.
    provideAppInitializer(() => inject(LanguageService).init()),
  ];
}
